import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { authApi } from '../../api';

const ResetPasswordContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9f9;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ResetPasswordCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 564px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    gap: 20px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 590;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  margin: 0 0 4px 0;
  letter-spacing: 0.352px;
  width: 100%;
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  letter-spacing: -0.154px;
  width: 100%;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  margin-bottom: 8px;
  letter-spacing: -0.0004px;
`;

const Input = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  padding: 10px 12px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  letter-spacing: -0.0004px;
  
  &::placeholder {
    color: #68737d;
  }
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &.error {
    border-color: #cc3340;
  }
`;

const ResetButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #cc3340;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const PasswordRequirements = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #68737d;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const RequirementItem = styled.div<{ met: boolean }>`
  color: ${props => props.met ? '#00a86b' : '#68737d'};
  margin: 2px 0;
  
  &::before {
    content: ${props => props.met ? '"✓"' : '"•"'};
    margin-right: 4px;
  }
`;

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const resetToken = location.state?.resetToken;
  const email = location.state?.email;

  useEffect(() => {
    if (!resetToken || !email) {
      navigate('/forgot-password');
      return;
    }
  }, [resetToken, email, navigate]);

  const passwordRequirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(password) },
    { text: 'Contains number', met: /\d/.test(password) },
    { text: 'Contains special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) }
  ];

  const validatePassword = (password: string) => {
    return passwordRequirements.every(req => req.met);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: {[key: string]: string} = {};
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(password)) {
      newErrors.password = 'Password does not meet requirements';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    try {
      // Step 3: Reset password using the new two-step flow
      await authApi.resetPassword(email, resetToken, password);
      setSuccess(true);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Failed to reset password. Please try again.' });
    }
    
    setLoading(false);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors({ ...errors, password: '' });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors({ ...errors, confirmPassword: '' });
    }
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  if (!resetToken) {
    return null;
  }

  if (success) {
    return (
      <ResetPasswordContainer>
        <ResetPasswordCard>
          <TitleSection>
            <Title>Password Reset Successful</Title>
            <Subtitle>
              Your password has been successfully reset. You can now sign in with your new password.
            </Subtitle>
          </TitleSection>
          
          <ResetButton type="button" onClick={handleSignInClick}>
            Go to Sign In
          </ResetButton>
        </ResetPasswordCard>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer>
      <ResetPasswordCard>
        <TitleSection>
          <Title>Reset Your Password</Title>
          <Subtitle>
            Enter your new password below. Make sure it meets all the requirements for security.
          </Subtitle>
        </TitleSection>
        
        <FormContainer onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={handlePasswordChange}
              className={errors.password ? "error" : ""}
            />
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            <PasswordRequirements>
              {passwordRequirements.map((req, index) => (
                <RequirementItem key={index} met={req.met}>
                  {req.text}
                </RequirementItem>
              ))}
            </PasswordRequirements>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              className={errors.confirmPassword ? "error" : ""}
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
          </InputGroup>
          
          {errors.submit && <ErrorMessage style={{ textAlign: 'center' }}>{errors.submit}</ErrorMessage>}
          
          <ResetButton type="submit" disabled={loading || !validatePassword(password) || password !== confirmPassword}>
            {loading ? "Resetting..." : "Reset Password"}
          </ResetButton>
        </FormContainer>
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};

export default ResetPassword;
